import type { EventAddress, EventFormValues, EventPayload } from './types'

/** Drop keys whose value is undefined so the request body stays minimal. */
const compact = <T extends object>(obj: T): Partial<T> => {
  const out: Partial<T> = {}
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined) {
      out[key as keyof T] = value as T[keyof T]
    }
  }
  return out
}

/** '' → undefined, so empty inputs are omitted rather than sent as blanks. */
const text = (value: string): string | undefined =>
  value.trim() === '' ? undefined : value.trim()

/** '' → undefined, otherwise a number. Country/province ids arrive as strings
 *  from CountryProvinceField, but the API expects numeric ids. */
const numeric = (value: string): number | undefined => {
  const trimmed = value.trim()
  if (trimmed === '') {
    return undefined
  }
  const parsed = Number(trimmed)
  return Number.isNaN(parsed) ? undefined : parsed
}

const RECURRING_TYPES = ['weekly', 'biweekly', 'monthly'] as const
type RecurringType = (typeof RECURRING_TYPES)[number]

const isRecurringType = (value: string): value is RecurringType =>
  (RECURRING_TYPES as readonly string[]).includes(value)

export interface BuildEventPayloadOptions {
  /**
   * Whether the form OFFERED the Team select — true only for a competition
   * event, where the teams endpoint has a schedule to scope to.
   *
   * Load-bearing, not cosmetic. Without it an update of a TEAM-owned event
   * would read its blank `values.teamId` (the field was never rendered, so
   * nothing filled it) and send `team_id: null`, unlinking the event from the
   * team it was created under. So the payload only speaks about `team_id` at
   * all when the admin actually had a say in it.
   */
  hasTeamField?: boolean
}

/**
 * Map Formik values onto the API request body.
 *
 * `mode` distinguishes create from update, because "empty" means different
 * things for each:
 * - 'create' (default): empty optional fields are omitted entirely; the
 *   address sub-object is omitted when every one of its fields is empty. A
 *   brand-new event genuinely has no value there.
 * - 'update': a cleared optional field (description / ends_at / address /
 *   arrival_time_minutes) is sent as an explicit `null` instead of being
 *   omitted, so the backend/mock knows to CLEAR the field rather than
 *   preserve the prior value. Address is only nulled when every one of its
 *   fields is empty; a partially-filled address is still sent as a compacted
 *   object.
 *
 * Recurrence contract:
 * - The *presence* of `recurrence_type` is what makes an event repeat. A
 *   one-off event ('none', the UI-only sentinel) omits `recurrence_type` and
 *   every other recurrence key entirely — never sends 'none' as a value.
 * - A repeating event sends `recurrence_type` plus `repeat_until` (a UTC ISO
 *   cut-off). The rule's START is `starts_at` — the event's first occurrence —
 *   so it needs no field of its own.
 * - `days_of_week` is required for weekly/biweekly and omitted for monthly.
 * - `arrival_time_minutes` is sent as a NUMBER when present; blank/non-numeric
 *   is omitted on create and nulled on update, matching description/ends_at.
 * - `timezone` is sent whenever present — see the location contract below for
 *   where its value comes from in each mode.
 *
 * Location contract — the two modes are MUTUALLY EXCLUSIVE:
 * - Venue mode (`venueId` set): sends `venue_id` + `facility_id`, and omits
 *   `address`/`address_name` — the venue record owns the location.
 * - Custom mode (`venueId` empty): sends the address block, and on update NULLS
 *   `venue_id`/`facility_id` so switching away from a venue actually unlinks it.
 *
 * `timezone` is sent in BOTH modes. Selecting a venue writes that venue's zone
 * onto the form, so venue mode sends the venue's own zone; a venue lacking one
 * falls back to the admin's pick, which stays visible for exactly that reason.
 *
 * Team contract — see `options.hasTeamField`.
 */
export const buildEventPayload = (
  values: EventFormValues,
  mode: 'create' | 'update' = 'create',
  options: BuildEventPayloadOptions = {}
): EventPayload => {
  const { hasTeamField = false } = options

  const address = compact({
    street_1: text(values.addressStreet1),
    street_2: text(values.addressStreet2),
    city: text(values.addressCity),
    postal_code: text(values.addressPostalCode),
    country_id: numeric(values.addressCountryId),
    province_id: numeric(values.addressProvinceId),
  }) as EventPayload['address']

  const payload: EventPayload = {
    name: values.name.trim(),
    starts_at: values.starts_at,
  }

  // A venue OWNS the location: its address and timezone come from the venue
  // record, so the two modes are mutually exclusive rather than additive.
  // Sending an address alongside a venue_id would leave the backend with two
  // competing sources of truth for where the event is.
  const venueId = text(values.venueId)
  const isVenueMode = venueId !== undefined

  // Sent as `details` — the name the API both accepts and returns. The Formik
  // field stays `description` because that is what the form/inputs are keyed
  // on; this is the single point where the two names meet.
  // Optional, and sent verbatim — same omit-on-create / null-on-update rule as
  // every other clearable field.
  const category = text(values.category)
  if (category !== undefined) {
    payload.category = category
  } else if (mode === 'update') {
    payload.category = null
  }

  const details = text(values.description)
  if (details !== undefined) {
    payload.details = details
  } else if (mode === 'update') {
    payload.details = null
  }

  // Only when the Team select was actually on the form — a team-owned event
  // must never send this key, least of all as `null` (see hasTeamField).
  // Within that gate it follows the same omit-on-create / null-on-update rule
  // as every other clearable field, so an admin can unassign a team and hand
  // the event back to the competition as a whole.
  if (hasTeamField) {
    const teamId = text(values.teamId)
    if (teamId !== undefined) {
      payload.team_id = teamId
    } else if (mode === 'update') {
      payload.team_id = null
    }
  }

  if (isVenueMode) {
    payload.venue_id = venueId
    // Facility is optional even with a venue — a location may have none, and
    // the picker allows leaving it unset.
    const facilityId = text(values.facilityId)
    if (facilityId !== undefined) {
      payload.facility_id = facilityId
    } else if (mode === 'update') {
      payload.facility_id = null
    }
  } else if (mode === 'update') {
    // Switching a venue-backed event to a custom address has to CLEAR the
    // venue, or the backend keeps the old one and ignores the address.
    payload.venue_id = null
    payload.facility_id = null
  }

  // Top-level field, NOT part of the `address` object — the API models the
  // location name separately from the postal address. Skipped entirely in
  // venue mode, where the venue supplies the name.
  const addressName = isVenueMode ? undefined : text(values.addressName)
  if (addressName !== undefined) {
    payload.address_name = addressName
  } else if (mode === 'update') {
    payload.address_name = null
  }

  const endsAt = text(values.ends_at)
  if (endsAt !== undefined) {
    payload.ends_at = endsAt
  } else if (mode === 'update') {
    payload.ends_at = null
  }

  if (!isVenueMode && address && Object.keys(address).length > 0) {
    payload.address = address
  } else if (mode === 'update') {
    payload.address = null
  }

  // Editing ONE occurrence cannot change the series' repeat rule, and the API
  // rejects a save that carries it alongside `scope=this`. The form hides
  // those fields for that scope, but their VALUES persist — switching to
  // 'All', editing the days, then back to 'This' would otherwise still send
  // them. Enforced here so the rule holds regardless of what the UI does.
  // Only applies to an update: create has no scope, and a one-off edit leaves
  // `editScope` unset.
  const scopeAllowsRecurrence = mode !== 'update' || values.editScope !== 'this'

  if (scopeAllowsRecurrence && isRecurringType(values.recurrenceType)) {
    payload.recurrence_type = values.recurrenceType

    if (values.recurrenceType !== 'monthly' && values.daysOfWeek.length > 0) {
      payload.days_of_week = values.daysOfWeek
    }

    // The rule's START comes from `starts_at` — the event's own first
    // occurrence — so only the cut-off needs its own field. `repeat_until`
    // replaced the former starts_on/ends_on/start_time/end_time quartet.
    const repeatUntil = text(values.repeatUntil)
    if (repeatUntil !== undefined) {
      payload.repeat_until = repeatUntil
    }
  }

  const arrivalTimeMinutes = numeric(values.arrivalTimeMinutes)
  if (arrivalTimeMinutes !== undefined) {
    payload.arrival_time_minutes = arrivalTimeMinutes
  } else if (mode === 'update') {
    payload.arrival_time_minutes = null
  }

  // Always sent, in every mode.
  //
  // In venue mode this IS the venue's zone: selecting a location writes it into
  // `timezone` (see EventLocationFields), so the value here already reflects
  // the venue. A venue without a zone of its own keeps the Timezone select
  // visible and sends the admin's choice instead.
  if (text(values.timezone) !== undefined) {
    payload.timezone = values.timezone.trim()
  }

  return payload
}

export type { EventAddress }
